import { useCallback, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, ClickAwayListener, Drawer, useMediaQuery } from '@mui/material';

import {
  mobileCloseBtnContainerSx,
  mobileDrawerBackdropSx,
  mobileDrawerPaperSx,
  paperInnerScrollSx,
  paperSx,
} from './sidebar.styles';
import { SidebarMenu } from './sidebar.types';
import { SidebarInner } from './SidebarInner';
import { SidebarMobileTopBar, MobileNavItem } from './SidebarMobileTopBar';
export type { MobileNavItem } from './SidebarMobileTopBar';

type BackrsSidebarProps = Readonly<{
  sidebar: SidebarMenu;
  isEmulating?: boolean;
  controlledDesktopOpen?: boolean;
  onDesktopOpenChange?: (open: boolean) => void;
  controlledMobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
  mobileNavItems?: MobileNavItem[];
}>;

export const BackrsSidebar = ({
  sidebar,
  isEmulating = false,
  controlledDesktopOpen,
  onDesktopOpenChange,
  controlledMobileOpen,
  onMobileOpenChange,
  mobileNavItems,
}: BackrsSidebarProps) => {
  const [uncontrolledDesktopOpen, setUncontrolledDesktopOpen] = useState(false);
  const [uncontrolledMobileOpen, setUncontrolledMobileOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState<string>();

  const isMobile = useMediaQuery('(max-width: 600px)');

  const desktopOpen = controlledDesktopOpen ?? uncontrolledDesktopOpen;
  const setDesktopOpen = useCallback(
    (open: boolean) => {
      onDesktopOpenChange?.(open);
      if (controlledDesktopOpen === undefined) setUncontrolledDesktopOpen(open);

      setExpandedItem(undefined);
    },
    [controlledDesktopOpen, onDesktopOpenChange],
  );

  const mobileOpen = controlledMobileOpen ?? uncontrolledMobileOpen;
  const setMobileOpen = useCallback(
    (open: boolean) => {
      onMobileOpenChange?.(open);
      if (controlledMobileOpen === undefined) setUncontrolledMobileOpen(open);

      setExpandedItem(undefined);
    },
    [controlledMobileOpen, onMobileOpenChange],
  );

  const handleToggleDesktop = useCallback(
    () => setDesktopOpen(!desktopOpen),
    [desktopOpen, setDesktopOpen],
  );
  const handleOpenMobile = useCallback(() => setMobileOpen(true), [setMobileOpen]);
  const handleCloseMobile = useCallback(() => setMobileOpen(false), [setMobileOpen]);

  const handleSettingExpandedItem = useCallback(
    (itemName?: string, forceOpen?: boolean) => {
      if (forceOpen) {
        if (isMobile) {
          setMobileOpen(true);
        } else {
          setDesktopOpen(true);
        }
      }

      setExpandedItem(itemName);
    },
    [isMobile, setDesktopOpen, setMobileOpen],
  );

  const handleSearchEnterKey = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;

      const value = (event.target as HTMLInputElement).value;

      sidebar.search.onSearch(value);
      (event.target as HTMLInputElement).value = '';

      if (isMobile) {
        handleCloseMobile();

        return;
      }

      setDesktopOpen(false);
    },
    [isMobile, setDesktopOpen, handleCloseMobile, sidebar.search],
  );

  if (isMobile) {
    return (
      <>
        <SidebarMobileTopBar
          onMenuClick={() => (mobileOpen ? handleCloseMobile() : handleOpenMobile())}
          onLogoClick={() => {
            sidebar.goToHome();
            if (mobileOpen) handleCloseMobile();
          }}
          isEmulating={isEmulating}
          mobileNavItems={mobileNavItems}
        />
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleCloseMobile}
          ModalProps={{
            keepMounted: true,
            BackdropProps: { sx: mobileDrawerBackdropSx },
          }}
          PaperProps={{ sx: mobileDrawerPaperSx }}
        >
          <Box id="sidebar-close-button" sx={mobileCloseBtnContainerSx} onClick={handleCloseMobile}>
            <AppIcon iconName="XClose" diameter="24px" color="Colors/Base/White" />
          </Box>

          <Box sx={paperInnerScrollSx(false)}>
            <SidebarInner
              sidebar={sidebar}
              sidebarOpen={true} // mobile drawer is always expanded-looking
              isMobile={isMobile}
              isEmulating={isEmulating}
              expandedItem={expandedItem}
              setExpandedItem={handleSettingExpandedItem}
              handleToggleDesktop={handleToggleDesktop}
              handleCloseMobile={handleCloseMobile}
              handleSearchEnterKey={handleSearchEnterKey}
              handleCloseDesktop={() => setDesktopOpen(false)}
            />
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={() => desktopOpen && setDesktopOpen(false)}
    >
      <Box sx={{ display: 'contents' }}>
        <Drawer
          variant="permanent"
          open={desktopOpen}
          PaperProps={{ sx: paperSx(desktopOpen, isEmulating) }}
        >
          <Box sx={paperInnerScrollSx(isEmulating)}>
            <SidebarInner
              sidebar={sidebar}
              sidebarOpen={desktopOpen}
              isMobile={isMobile}
              isEmulating={isEmulating}
              expandedItem={expandedItem}
              setExpandedItem={handleSettingExpandedItem}
              handleToggleDesktop={handleToggleDesktop}
              handleCloseMobile={handleCloseMobile}
              handleSearchEnterKey={handleSearchEnterKey}
              handleCloseDesktop={() => setDesktopOpen(false)}
            />
          </Box>
        </Drawer>
      </Box>
    </ClickAwayListener>
  );
};
